import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, Loader2, ExternalLink } from 'lucide-react';
import { vendorAdvertisementApi } from '../../services/api';
import { Advertisement, VendorAdvertisementUpdate } from '../../types';
import { extractApiError } from '../../utils/errorUtils';

interface EditAdvertisementModalProps {
  advertisement: Advertisement;
  onClose: () => void;
  onSave: (updatedAd: Advertisement) => void;
}

export default function EditAdvertisementModal({
  advertisement,
  onClose,
  onSave
}: EditAdvertisementModalProps) {
  const [formData, setFormData] = useState<VendorAdvertisementUpdate>({
    title: advertisement.title,
    description: advertisement.description || '',
    target_url: advertisement.target_url,
    image_url: advertisement.image_url || '',
    thumbnail_url: advertisement.thumbnail_url || '',
    display_text: advertisement.display_text || ''
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [imagePreview, setImagePreview] = useState<string | null>(advertisement.image_url || null);

  // Update image preview when image_url changes
  useEffect(() => {
    if (formData.image_url) {
      setImagePreview(formData.image_url);
    }
  }, [formData.image_url]);

  // Handle field change
  const handleChange = (field: keyof VendorAdvertisementUpdate, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.title || formData.title.trim().length === 0) {
      errors.title = 'Title is required';
    } else if (formData.title.length > 255) {
      errors.title = 'Title must be 255 characters or less';
    }

    if (formData.target_url && formData.target_url.length > 500) {
      errors.target_url = 'Target URL must be 500 characters or less';
    }

    if (formData.target_url) {
      try {
        new URL(formData.target_url);
      } catch {
        errors.target_url = 'Invalid URL format';
      }
    }

    if (formData.image_url && formData.image_url.length > 500) {
      errors.image_url = 'Image URL must be 500 characters or less';
    }

    if (formData.thumbnail_url && formData.thumbnail_url.length > 500) {
      errors.thumbnail_url = 'Thumbnail URL must be 500 characters or less';
    }

    if (formData.display_text && formData.display_text.length > 255) {
      errors.display_text = 'Display text must be 255 characters or less';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Only send non-empty fields
      const updateData: VendorAdvertisementUpdate = {};
      if (formData.title) updateData.title = formData.title;
      if (formData.description) updateData.description = formData.description;
      if (formData.target_url) updateData.target_url = formData.target_url;
      if (formData.image_url) updateData.image_url = formData.image_url;
      if (formData.thumbnail_url) updateData.thumbnail_url = formData.thumbnail_url;
      if (formData.display_text) updateData.display_text = formData.display_text;

      const updatedAd = await vendorAdvertisementApi.updateAdvertisement(
        advertisement.id,
        updateData
      );

      onSave(updatedAd);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to update advertisement:', apiError);
      setError(apiError.message);
    } finally {
      setSaving(false);
    }
    // validateForm uses stable state setters, safe to omit from dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, advertisement.id, onSave]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        void handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [formData, handleSave, onClose]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      data-testid="edit-advertisement-modal"
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Edit Advertisement</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          className="p-6 space-y-6"
          data-testid="edit-advertisement-form"
        >
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Vendor-only fields notice */}
          <div className="bg-secondary-light/10 border border-primary-light text-primary-dark px-4 py-3 rounded-lg text-sm">
            <strong>Note:</strong> You can only edit content fields. Ad type, priority, and dates are managed by administrators.
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                validationErrors.title ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter advertisement title"
              maxLength={255}
            />
            {validationErrors.title && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.title}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {formData.title.length}/255 characters
            </p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter advertisement description"
            />
          </div>

          {/* Target URL */}
          <div>
            <label htmlFor="target_url" className="block text-sm font-medium text-gray-700 mb-1">
              Target URL
            </label>
            <div className="relative">
              <input
                type="url"
                id="target_url"
                value={formData.target_url}
                onChange={(e) => handleChange('target_url', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10 ${
                  validationErrors.target_url ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="https://example.com"
                maxLength={500}
              />
              {formData.target_url && (
                <a
                  href={formData.target_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-primary"
                >
                  <ExternalLink className="h-5 w-5" />
                </a>
              )}
            </div>
            {validationErrors.target_url && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.target_url}</p>
            )}
          </div>

          {/* Image URL */}
          <div>
            <label htmlFor="image_url" className="block text-sm font-medium text-gray-700 mb-1">
              Image URL
            </label>
            <input
              type="url"
              id="image_url"
              value={formData.image_url}
              onChange={(e) => handleChange('image_url', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                validationErrors.image_url ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="https://example.com/image.jpg"
              maxLength={500}
            />
            {validationErrors.image_url && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.image_url}</p>
            )}

            {/* Image Preview */}
            {imagePreview && (
              <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
                <p className="text-xs font-medium text-gray-700 mb-2">Preview:</p>
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-48 rounded-lg mx-auto"
                  onError={() => setImagePreview(null)}
                />
              </div>
            )}
          </div>

          {/* Thumbnail URL */}
          <div>
            <label htmlFor="thumbnail_url" className="block text-sm font-medium text-gray-700 mb-1">
              Thumbnail URL
            </label>
            <input
              type="url"
              id="thumbnail_url"
              value={formData.thumbnail_url}
              onChange={(e) => handleChange('thumbnail_url', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                validationErrors.thumbnail_url ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="https://example.com/thumbnail.jpg"
              maxLength={500}
            />
            {validationErrors.thumbnail_url && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.thumbnail_url}</p>
            )}
          </div>

          {/* Display Text */}
          <div>
            <label htmlFor="display_text" className="block text-sm font-medium text-gray-700 mb-1">
              Display Text
            </label>
            <input
              type="text"
              id="display_text"
              value={formData.display_text}
              onChange={(e) => handleChange('display_text', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                validationErrors.display_text ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g., Watch Now, Learn More"
              maxLength={255}
            />
            {validationErrors.display_text && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.display_text}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {formData.display_text.length}/255 characters
            </p>
          </div>

          {/* Read-only fields info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Read-Only Information</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-600">Ad Type:</span>
                <span className="ml-2 font-medium text-gray-900">{advertisement.ad_type}</span>
              </div>
              <div>
                <span className="text-gray-600">Priority:</span>
                <span className="ml-2 font-medium text-gray-900">{advertisement.priority}</span>
              </div>
              <div>
                <span className="text-gray-600">Status:</span>
                <span className={`ml-2 font-medium ${advertisement.is_active ? 'text-green-600' : 'text-red-600'}`}>
                  {advertisement.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Impressions:</span>
                <span className="ml-2 font-medium text-gray-900">{advertisement.impressions.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              data-testid="edit-advertisement-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>

          {/* Keyboard shortcuts hint */}
          <div className="text-xs text-gray-500 text-center pt-2 border-t border-gray-100">
            Press <kbd className="px-2 py-1 bg-gray-100 rounded">Esc</kbd> to cancel or{' '}
            <kbd className="px-2 py-1 bg-gray-100 rounded">Ctrl/Cmd+Enter</kbd> to save
          </div>
        </form>
      </div>
    </div>
  );
}
